<template>
  <div class="settings">

    <header class="masthead">
      <h1 class="title">Settings<span class="dot">.</span></h1>
      <p class="sub">How the dashboard behaves when you open it.</p>
    </header>

    <p v-if="loading" class="msg">Reading your settings</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <template v-else>
      <div class="setting">
        <label class="toggle">
          <input type="checkbox" :checked="rotate" @change="setRotate($event.target.checked)">
          <span class="track"><span class="knob"></span></span>
        </label>

        <div class="setting-text">
          <p class="setting-name">Cycle through countries</p>
          <p class="setting-note">
            On, the dashboard moves to the next country every 30 seconds. Off, it
            stays on one.
          </p>
        </div>
      </div>

      <!-- Only meaningful once cycling is off, so it appears then rather than
           sitting there greyed out inviting a click that does nothing. -->
      <div v-if="!rotate" class="setting indent">
        <div class="setting-text">
          <p class="setting-name">Show</p>
          <p class="setting-note">Which country to stay on.</p>
        </div>

        <select class="picker" :value="pinnedCountry" @change="setPinned($event.target.value)">
          <option value="">First available</option>
          <option v-for="c in countries" :key="c.code" :value="c.code">
            {{ c.name }}
          </option>
        </select>
      </div>

      <p v-if="saveError" class="msg err">{{ saveError }}</p>
      <p v-else-if="saved" class="msg ok">Saved</p>
    </template>

  </div>
</template>


<script setup>
import { ref, onMounted } from 'vue'
import { getSiteHierarchy } from '@/services/GraphService'
import { getMyDisplayPref, setMyDisplayPref } from '@/services/UserPrefService'

const rotate = ref(true)
const pinnedCountry = ref('')
const countries = ref([])

const loading = ref(true)
const error = ref(null)
const saveError = ref(null)
const saved = ref(false)

async function load() {
  loading.value = true
  error.value = null

  try {
    const [pref, hierarchy] = await Promise.all([
      getMyDisplayPref(),
      getSiteHierarchy()
    ])

    rotate.value = pref.rotate
    pinnedCountry.value = pref.pinnedCountry
    countries.value = hierarchy?.countries ?? []
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

async function save() {
  saveError.value = null
  saved.value = false

  try {
    await setMyDisplayPref({ rotate: rotate.value, pinnedCountry: pinnedCountry.value })
    saved.value = true
    setTimeout(() => { saved.value = false }, 2000)
  } catch (err) {
    console.error(err)
    saveError.value = err.message ?? String(err)
  }
}

// Applied then saved, rather than behind a Save button: there are two settings
// and waiting on a round trip for either makes them feel unresponsive.
async function setRotate(value) {
  rotate.value = value
  await save()
}

async function setPinned(value) {
  pinnedCountry.value = value
  await save()
}

onMounted(load)
</script>


<style scoped>
.settings {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --on: #0f9d58;

  /* Left aligned, not centred. Centring made the content drift right of the
     menu and the logo, so nothing lined up with anything else. The max-width
     stays: it stops a line of text running the width of a wide monitor. */
  max-width: 720px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 10px;
}

.title {
  font-size: clamp(28px, 3.8vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.sub {
  font-size: 14.5px;
  color: var(--ink-soft);
  margin: 8px 0 0;
}

/* ---------- Settings ---------- */

.setting {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 22px 0;
  border-bottom: 1px solid var(--line);
}

.setting:last-of-type { border-bottom: none; }

.setting.indent { padding-left: 60px; align-items: center; }

.setting-text { flex: 1; min-width: 0; }

.setting-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  margin: 0;
}

.setting-note {
  font-size: 14px;
  line-height: 1.5;
  color: var(--ink-soft);
  margin: 5px 0 0;
  max-width: 46ch;
}

/* ---------- Toggle ---------- */

.toggle {
  flex: none;
  cursor: pointer;
  margin-top: 2px;
}

.toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.track {
  display: block;
  width: 42px;
  height: 24px;
  border-radius: 12px;
  background: #cbd5dd;
  position: relative;
  transition: background 0.18s ease;
}

.knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  transition: transform 0.18s ease;
}

.toggle input:checked + .track { background: var(--on); }
.toggle input:checked + .track .knob { transform: translateX(18px); }

.toggle input:focus-visible + .track {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.picker {
  font-family: inherit;
  font-size: 14.5px;
  height: 42px;
  min-width: 200px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #ffffff;
  color: #191c1e;
  cursor: pointer;
  outline: none;
  flex: none;
}

.picker:focus { border-color: var(--ink); }

/* ---------- Messages ---------- */

.msg {
  font-size: 15px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
}

.msg.err { color: #b3261e; }
.msg.ok { color: var(--on); }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  margin-left: 10px;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 600px) {
  .settings { padding: 0 20px 60px; }
  .setting.indent { padding-left: 0; flex-direction: column; align-items: stretch; gap: 12px; }
  .picker { width: 100%; min-width: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .track, .knob { transition: none; }
}
</style>
